import type { ShellAppRegistry } from '@taki/shell';
import { entreeDeRegistre } from '@taki/app-runtime';
import { contactApp } from './apps/contact/taki.app.js';
import cockpitInfo from './apps/cockpit/taki.app.json';
import { timesheetApp } from './apps/timesheet/taki.app.js';
import { croquisApp } from './apps/croquis/taki.app.js';
import { canauxApp } from './apps/canaux/taki.app.js';
import { projetsApp } from './apps/projets/taki.app.js';
import { tachesApp } from './apps/taches/taki.app.js';
import { fluxApp } from './apps/flux/taki.app.js';
import { smartNotesApp } from './apps/smart-notes/taki.app.js';
import { demoTownApp } from './apps/demo-town/taki.app.js';
import { catalogueApp } from './apps/catalogue/taki.app.js';
import { patientsApp } from './apps/patients/taki.app.js';
import { consultationsApp } from './apps/consultations/taki.app.js';

/**
 * Registre des apps montees par le shell.
 *
 * DEUX REGIMES COEXISTENT ICI, et l'entree le dit d'un coup d'oeil.
 *  - DECLARE (toutes les apps metier) : l'app n'a pas d'ecran a elle. Elle
 *    donne ses trois fichiers a `definirAppDeclaree`, et `entreeDeRegistre`
 *    (`@taki/app-runtime`)
 *    en derive tout : l'ecran generique du socle les interprete au runtime.
 *    C'EST LA CIBLE de toutes les apps.
 *  - ECRIT A LA MAIN (le seul `cockpit`, outil interne) : un ecran et ses
 *    reglages a lui, montes par `load()` et `loadSettings()`.
 * Une app RETIREE du produit sort d'ici en meme temps que son dossier : le
 * registre est la seule liste qui monte les apps, il ne garde jamais une entree
 * commentee ni un import mort.
 *
 * UNE ENTREE PORTE SA FICHE, jamais une copie de ce qu'elle contient. Le
 * libelle du dock (`menu.label`) et l'icone (`icon`) vivaient ici ET dans
 * `taki.app.json` : le Builder n'ecrivait que la fiche, et le dock gardait
 * l'ancien nom. Le shell derive les deux en un seul endroit
 * (`resolveAppEntry`, `packages/shell/src/appRegistry.ts`). Renommer une app,
 * ou changer son icone, c'est donc modifier sa fiche, et rien d'autre.
 *
 * Chaque app se distingue par son ICONE, jamais par une couleur de fond ; une
 * app deprecated se declare par les champs `deprecated` / `deprecatedSince` de
 * sa fiche, jamais par un marqueur ecrit a la main dans un texte affiche
 * (`pnpm apps:check` echoue si le marqueur reapparait quelque part).
 *
 * Le numero de version n'a rien a faire ici non plus : la fiche est la seule
 * source de verite, et c'est l'outil de release qui l'ecrit (docs/PLAYBOOK-APP.md section 4).
 *
 * Les ids correspondent aux entrees de `orgs/{orgId}/settings/apps` en base.
 * Le shell monte chaque app paresseusement via `load()`, ses reglages via
 * `loadSettings()` (hub Parametres, /settings).
 *
 * LES RACCOURCIS DU DOCK VIENNENT DES PORTES : chaque creation declaree dans
 * `taki.screens.json` en devient un, et le shell le derive seul pour une app
 * DECLAREE (sa declaration voyage avec l'entree). Une app ECRITE A LA MAIN ne
 * la transmet pas : elle passerait son fichier d'ecrans a `actionsDesPortes`
 * (`@taki/shell`). Seul le cockpit, qui n'a pas de portes declarees, ecrit
 * encore les siens.
 */
export const demoRegistry: ShellAppRegistry = {
  'consultations': entreeDeRegistre(consultationsApp),
  'patients': entreeDeRegistre(patientsApp),
  'catalogue': entreeDeRegistre(catalogueApp),
  'demo-town': entreeDeRegistre(demoTownApp),
  'flux': entreeDeRegistre(fluxApp),
  'smart-notes': entreeDeRegistre(smartNotesApp),
  'taches': entreeDeRegistre(tachesApp),
  'projets': entreeDeRegistre(projetsApp),
  'canaux': entreeDeRegistre(canauxApp),
  'croquis': entreeDeRegistre(croquisApp),
  'timesheet': entreeDeRegistre(timesheetApp),
  /*
   * CONTACT EST EN REGIME DECLARE : son entree est DERIVEE de sa declaration
   * (`entreeDeRegistre`, `@taki/app-runtime`), elle ne cite ni ecran ni panneau
   * de reglages. L'app n'a plus de `ContactScreen.tsx` ni de `views.ts` : c'est
   * l'ecran generique du socle qui interprete ses trois fichiers.
   */
  contact: entreeDeRegistre(contactApp),
  cockpit: {
    id: 'cockpit',
    fiche: cockpitInfo,
    /*
     * SEULE ENTREE EN IMPORT PARESSEUX du registre, et c'est voulu : le cockpit
     * pese plusieurs milliers de lignes qui ne servent qu'a nous. Un import
     * statique les embarquerait dans le bundle de CHAQUE client, meme la ou
     * l'app n'est pas installee. `import()` les met dans un morceau a part, que
     * le navigateur ne va chercher que si on ouvre l'app.
     *
     * Sa vraie porte de sortie reste `installedApps` du `taki.config.json` :
     * une config client qui ne cite pas `cockpit` ne le monte jamais.
     */
    load: () => import('./apps/cockpit/CockpitScreen.js'),
    loadSettings: () => import('./apps/cockpit/settings.js'),
    actions: [
      { id: 'new-chantier', label: 'Nouveau chantier', iconName: 'IconPlus' },
      { id: 'new-brouillon', label: "Nouvelle idée d'app", iconName: 'IconPlus' },
    ],
  },
};

export default demoRegistry;
